import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { Warning, CheckCircle, Link as LinkIcon, ForkKnife, ArrowsClockwise, Users, ChatTeardropDots } from "@phosphor-icons/react";
import SmsBroadcastModal from "./SmsBroadcastModal";

const TZ_LABEL = "America/New_York";

function isoLocalDate(d = new Date()) {
  // Convert browser "now" → YYYY-MM-DD in America/New_York
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/New_York",
    year: "numeric", month: "2-digit", day: "2-digit",
  });
  return fmt.format(d); // en-CA gives ISO-ish YYYY-MM-DD
}

function shiftDate(isoDate, deltaDays) {
  const [y, m, d] = isoDate.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + deltaDays);
  return dt.toISOString().slice(0, 10);
}

function prettyDate(iso) {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(undefined, {
    weekday: "long", month: "long", day: "numeric", year: "numeric", timeZone: "UTC",
  });
}

function GameLinkBadge({ state }) {
  if (state === "sent") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-ms-success/15 text-ms-success" data-testid="game-link-badge-sent">
        <CheckCircle size={12} weight="fill" /> Link sent
      </span>
    );
  }
  if (state === "pasted") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-ms-warn/20 text-ms-warn" data-testid="game-link-badge-pasted">
        <LinkIcon size={12} weight="fill" /> Pasted · queued
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-ms-danger/15 text-ms-danger" data-testid="game-link-badge-missing">
      <Warning size={12} weight="fill" /> Paste link
    </span>
  );
}

function Stat({ label, value, tone = "default", testid }) {
  const tones = {
    default: "text-ms-earth",
    warn: "text-ms-warn",
    danger: "text-ms-danger",
    success: "text-ms-success",
  };
  return (
    <div data-testid={testid}>
      <p className="label-eyebrow text-ms-stone text-[10px]">{label}</p>
      <p className={`serif text-xl mt-1 ${tones[tone] || tones.default}`}>{value}</p>
    </div>
  );
}

export default function TodayPanel() {
  const [date, setDate] = useState(isoLocalDate());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (d) => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/tours/today?date=${d}`);
      setData(r.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const r = await api.get(`/admin/tours/today?date=${date}`);
      if (!cancelled) setData(r.data);
    })();
    return () => { cancelled = true; };
  }, [date]);

  const reload = () => load(date);

  const isToday = date === isoLocalDate();
  const instances = data?.instances || [];
  const totalBookings = instances.reduce((s, i) => s + i.totals.bookings, 0);
  const totalHead = instances.reduce((s, i) => s + i.totals.headcount, 0);
  const missingLinks = instances.reduce((s, i) => s + i.totals.game_link_missing, 0);
  const dietaryCount = instances.reduce((s, i) => s + i.totals.dietary_flag_count, 0);

  const [smsInstance, setSmsInstance] = useState(null);

  return (
    <section className="mt-10 bg-white border border-ms-edge rounded-2xl overflow-hidden" data-testid="today-panel">
      <header className="flex items-start justify-between flex-wrap gap-4 p-6 border-b border-ms-edge bg-ms-cream/40">
        <div>
          <p className="label-eyebrow text-ms-stone text-xs flex items-center gap-2">
            Mission control {isToday && <span className="px-1.5 py-0.5 rounded bg-ms-earth text-ms-cream text-[9px]">LIVE</span>}
          </p>
          <h2 className="serif text-2xl text-ms-earth mt-1" data-testid="today-panel-date">
            {isToday ? "Today" : prettyDate(date)}
          </h2>
          <p className="text-xs text-ms-stone mt-1">{prettyDate(date)} · {TZ_LABEL}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setDate(shiftDate(date, -1))} className="px-3 py-1.5 text-xs border border-ms-edge rounded-md hover:bg-ms-cream" data-testid="today-prev">← Prev</button>
          <button onClick={() => setDate(isoLocalDate())} className="px-3 py-1.5 text-xs border border-ms-edge rounded-md hover:bg-ms-cream" data-testid="today-now">Today</button>
          <button onClick={() => setDate(shiftDate(date, 1))} className="px-3 py-1.5 text-xs border border-ms-edge rounded-md hover:bg-ms-cream" data-testid="today-next">Next →</button>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value || isoLocalDate())}
            className="ml-1 border border-ms-edge rounded-md px-2 py-1.5 text-xs"
            data-testid="today-date-input"
          />
          <button onClick={reload} className="ml-1 p-1.5 border border-ms-edge rounded-md hover:bg-ms-cream" data-testid="today-refresh" title="Refresh">
            <ArrowsClockwise size={14} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </header>

      {/* Day-level summary */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-6 p-6 border-b border-ms-edge">
        <Stat label="Tours" value={instances.length} testid="today-stat-tours" />
        <Stat label="Bookings" value={totalBookings} testid="today-stat-bookings" />
        <Stat label="Guests" value={totalHead} testid="today-stat-guests" />
        <Stat label="Missing links" value={missingLinks} tone={missingLinks ? "danger" : "default"} testid="today-stat-missing" />
        <Stat label="Dietary flags" value={dietaryCount} tone={dietaryCount ? "warn" : "default"} testid="today-stat-dietary" />
      </div>

      {/* Per-instance cards */}
      <div className="divide-y divide-ms-edge">
        {instances.length === 0 ? (
          <div className="p-10 text-center text-ms-stone text-sm" data-testid="today-empty">
            {loading ? "Loading…" : `No tours scheduled for ${prettyDate(date)}.`}
          </div>
        ) : (
          instances.map((inst) => (
            <InstanceCard key={inst.instance_id} inst={inst} onMessageParty={() => setSmsInstance(inst)} />
          ))
        )}
      </div>

      {smsInstance && (
        <SmsBroadcastModal
          instance={smsInstance}
          onClose={() => setSmsInstance(null)}
          onSent={() => load(date)}
        />
      )}
    </section>
  );
}

function InstanceCard({ inst, onMessageParty }) {
  const t = inst.totals;
  const hasUrgent = t.game_link_missing > 0;
  return (
    <article className={`p-6 ${hasUrgent ? "bg-ms-danger/5" : ""}`} data-testid={`today-instance-${inst.instance_id}`}>
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <p className="serif text-xl text-ms-earth">{inst.starts_at_local} · {inst.tour_title}</p>
          {inst.meeting_location && <p className="text-xs text-ms-stone mt-0.5">{inst.meeting_location}</p>}
        </div>
        <div className="flex items-center gap-4 text-sm">
          <span className="inline-flex items-center gap-1 text-ms-earth" data-testid={`today-instance-${inst.instance_id}-headcount`}>
            <Users size={14} /> {t.headcount}/{inst.capacity}
          </span>
          {t.dietary_flag_count > 0 && (
            <span className="inline-flex items-center gap-1 text-ms-warn" data-testid={`today-instance-${inst.instance_id}-diet-count`}>
              <ForkKnife size={14} /> {t.dietary_flag_count}
            </span>
          )}
          {hasUrgent && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-ms-danger/15 text-ms-danger" data-testid={`today-instance-${inst.instance_id}-urgent`}>
              <Warning size={12} weight="fill" /> {t.game_link_missing} missing link{t.game_link_missing > 1 ? "s" : ""}
            </span>
          )}
          <button
            onClick={onMessageParty}
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs border border-ms-earth/40 text-ms-earth hover:bg-ms-earth hover:text-ms-cream transition-colors"
            data-testid={`today-instance-${inst.instance_id}-sms`}
            title="Send an SMS to the lead + co-guests on this tour"
          >
            <ChatTeardropDots size={12} /> Message party
          </button>
        </div>
      </div>

      {/* Bookings table */}
      <div className="mt-4 overflow-hidden border border-ms-edge rounded-xl">
        <table className="w-full text-sm">
          <thead className="bg-ms-cream/60 border-b border-ms-edge">
            <tr>
              <th className="text-left p-3 label-eyebrow text-[10px] text-ms-stone">Party</th>
              <th className="text-left p-3 label-eyebrow text-[10px] text-ms-stone">Guests</th>
              <th className="text-left p-3 label-eyebrow text-[10px] text-ms-stone">Game link</th>
              <th className="text-left p-3 label-eyebrow text-[10px] text-ms-stone">Dietary</th>
              <th className="text-left p-3 label-eyebrow text-[10px] text-ms-stone">Balance</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {inst.bookings.map((b) => (
              <tr key={b.id} className="border-t border-ms-edge last:border-b-0" data-testid={`today-booking-${b.id}`}>
                <td className="p-3 align-top">
                  <p className="text-ms-earth">{b.customer_name}</p>
                  <p className="text-xs text-ms-stone">{b.customer_email}</p>
                  {b.customer_phone && <p className="text-xs text-ms-stone">{b.customer_phone}</p>}
                </td>
                <td className="p-3 align-top text-ms-earth">{b.guest_count}</td>
                <td className="p-3 align-top">
                  <GameLinkBadge state={b.game_link_state} />
                </td>
                <td className="p-3 align-top">
                  {b.dietary_flags.length === 0 ? (
                    <span className="text-xs text-ms-stone">—</span>
                  ) : (
                    <ul className="space-y-1">
                      {b.dietary_flags.map((d, i) => (
                        <li key={i} className="text-xs">
                          <span className="text-ms-earth">{d.name}:</span> <span className="text-ms-stone">{d.note}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </td>
                <td className="p-3 align-top text-ms-earth">
                  {b.balance_outstanding > 0 ? (
                    <span className="text-ms-warn" data-testid={`today-booking-${b.id}-outstanding`}>${b.balance_outstanding.toFixed(2)} due</span>
                  ) : (
                    <span className="text-ms-success">Paid</span>
                  )}
                  <p className="text-[10px] text-ms-stone mt-0.5">{b.payment_mode}{b.status !== "confirmed" ? ` · ${b.status}` : ""}</p>
                </td>
                <td className="p-3 align-top text-right">
                  <Link
                    to={`/admin/bookings?focus=${b.id}`}
                    className="text-xs text-ms-brass hover:text-ms-brass-lo label-eyebrow"
                    data-testid={`today-booking-${b.id}-open`}
                  >
                    Open →
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </article>
  );
}
